import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/ui/SectionHeading'
import { BeforeAfterCarousel, type BeforeAfterPair } from './BeforeAfterCarousel'

interface TreatmentIntroProps {
  title?: string
  paragraphs: string[]
  image?: string
  imageAlt?: string
  beforeAfter?: BeforeAfterPair[]
  beforeLabel?: string
  afterLabel?: string
  videoUrl?: string
  className?: string
}

function getYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^?&]+)/
  )
  return match?.[1] ?? null
}

function YouTubeEmbed({ url }: { url: string }) {
  const id = getYouTubeId(url)
  if (!id) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center aspect-video w-full rounded-xl bg-ink-deep text-cream hover:bg-ink transition-colors"
      >
        Guarda il video su YouTube
      </a>
    )
  }

  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-ink-deep">
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}`}
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  )
}

export function TreatmentIntro({
  title,
  paragraphs,
  image,
  imageAlt,
  beforeAfter,
  beforeLabel,
  afterLabel,
  videoUrl,
  className,
}: TreatmentIntroProps) {
  return (
    <section className={cn('bg-cream py-16 md:py-24', className)}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6 }}
          >
            {title && <SectionHeading title={title} />}
            <div className={cn('space-y-4', title && 'mt-6')}>
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="text-muted leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </motion.div>

          {videoUrl ? (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6 }}
            >
              <YouTubeEmbed url={videoUrl} />
            </motion.div>
          ) : beforeAfter && beforeAfter.length > 0 ? (
            <BeforeAfterCarousel
              pairs={beforeAfter}
              beforeLabel={beforeLabel}
              afterLabel={afterLabel}
            />
          ) : image ? (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6 }}
              className="aspect-[4/3] rounded-xl overflow-hidden"
            >
              <img
                src={image}
                alt={imageAlt || ''}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </motion.div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
